import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, Platform, FlatList, Linking } from 'react-native';
import AppTextInput from './AppTextInput';
import { Business } from '../types/business';
import { appointmentRequestService } from '../services/appointmentRequestService';
import { AuthManager } from '../utils/authManager';
import DatePicker from './DatePicker';
import TimePicker from './TimePicker';
import { getServiceSuggestions, searchServices } from '../utils/serviceSuggestions';
import { customServiceRequestService } from '../services/customServiceRequestService';
import AutomotiveServiceIntakeForm from './AutomotiveServiceIntakeForm';
import MyVehiclesModal from './MyVehiclesModal';
import { isAutomotiveBusinessCategory } from '../utils/automotiveCategoryUtils';
import { vehicleGarageService } from '../services/vehicleGarageService';
import type { VehicleProfile } from '../types/vehicle';
import {
  appendAutomotiveDetailsToInstructions,
  automotiveIntakeFromVehicle,
  emptyAutomotiveIntake,
  MANUAL_VEHICLE_ENTRY,
  validateAutomotiveIntake,
  vehicleMatchesAutomotiveIntake,
  type AutomotiveIntakeData,
} from '../types/automotiveIntake';

interface AppointmentRequestFormProps {
  business: Business;
  requestType?: 'appointment' | 'estimate';
  onCancel: () => void;
  onSubmit: () => void;
}

const AppointmentRequestForm: React.FC<AppointmentRequestFormProps> = ({
  business,
  requestType = 'appointment',
  onCancel,
  onSubmit,
}) => {
  const [service, setService] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState({
    name: '',
    phone: '',
    email: '',
    userId: '',
  });
  
  // Service suggestions
  const [serviceSuggestions, setServiceSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const isAutomotiveRequest = isAutomotiveBusinessCategory(business.category);
  const [automotiveIntake, setAutomotiveIntake] = useState<AutomotiveIntakeData>(emptyAutomotiveIntake());
  const [savedVehicles, setSavedVehicles] = useState<VehicleProfile[]>([]);
  const [selectedAutomotiveVehicleId, setSelectedAutomotiveVehicleId] = useState<string | null>(null);
  const [showVehiclesModal, setShowVehiclesModal] = useState(false);

  useEffect(() => {
    loadUserInfo();
    loadServiceSuggestions();
    const businessWithPrefill = business as any;
    
    // Pre-fill service if provided
    if (businessWithPrefill.prefillService) {
      setService(businessWithPrefill.prefillService);
    }
    
    // Pre-fill date if provided
    if (businessWithPrefill.prefillDate) {
      // Already in YYYY-MM-DD format
      setDate(businessWithPrefill.prefillDate);
    } else if (businessWithPrefill.preferredDate) {
      // Convert date text to proper format if needed
      const dateValue = businessWithPrefill.preferredDate;
      if (dateValue === 'tomorrow' || dateValue === 'Tomorrow') {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setDate(tomorrow.toISOString().split('T')[0]);
      } else if (dateValue && dateValue.includes('-')) {
        // Already in YYYY-MM-DD format
        setDate(dateValue);
      }
    }
    
    // Pre-fill time if provided
    if (businessWithPrefill.prefillTime) {
      setTime(businessWithPrefill.prefillTime);
    }
  }, []);

  useEffect(() => {
    if (!isAutomotiveRequest || !userInfo.userId) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await vehicleGarageService.listVehicles(userInfo.userId);
        if (cancelled) return;
        setSavedVehicles(list);
        if (selectedAutomotiveVehicleId != null) return;
        const def = list.find((v) => v.isDefault) || list[0];
        if (def) {
          setSelectedAutomotiveVehicleId(def.id);
          setAutomotiveIntake((prev) => ({ ...prev, ...automotiveIntakeFromVehicle(def) }));
        } else {
          setSelectedAutomotiveVehicleId(MANUAL_VEHICLE_ENTRY);
        }
      } catch {
        if (!cancelled) {
          setSelectedAutomotiveVehicleId(MANUAL_VEHICLE_ENTRY);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAutomotiveRequest, userInfo.userId, selectedAutomotiveVehicleId]);

  const handleSelectSavedVehicleForIntake = (vehicle: VehicleProfile) => {
    setSelectedAutomotiveVehicleId(vehicle.id);
    setAutomotiveIntake((prev) => ({ ...prev, ...automotiveIntakeFromVehicle(vehicle) }));
  };

  const handleChooseManualAutomotiveEntry = () => {
    setSelectedAutomotiveVehicleId(MANUAL_VEHICLE_ENTRY);
  };

  const handleAutomotiveIntakeChange = (next: AutomotiveIntakeData) => {
    setAutomotiveIntake(next);
    if (
      selectedAutomotiveVehicleId &&
      selectedAutomotiveVehicleId !== MANUAL_VEHICLE_ENTRY
    ) {
      const vehicle = savedVehicles.find((v) => v.id === selectedAutomotiveVehicleId);
      if (vehicle && !vehicleMatchesAutomotiveIntake(vehicle, next)) {
        setSelectedAutomotiveVehicleId(MANUAL_VEHICLE_ENTRY);
      }
    }
  };

  // Load service suggestions based on business category
  const loadServiceSuggestions = () => {
    if (business.category) {
      // Try to match category ID
      const categoryId = business.category.toLowerCase().replace(/\s+/g, '_');
      const suggestions = getServiceSuggestions(categoryId);
      setServiceSuggestions(suggestions);
      setFilteredSuggestions(suggestions);
    }
  };

  // Handle service input change with auto-complete
  const handleServiceChange = (text: string) => {
    setService(text);
    
    if (text.length > 0) {
      // Search suggestions
      const matches = searchServices(text, business.category);
      setFilteredSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      // Show all suggestions when empty
      setFilteredSuggestions(serviceSuggestions);
      setShowSuggestions(serviceSuggestions.length > 0);
    }
  };

  // Handle service suggestion selection
  const handleSuggestionSelect = (suggestion: string) => {
    setService(suggestion);
    setShowSuggestions(false);
  };

  // Close suggestions when clicking outside
  const handleBlur = () => {
    // Delay to allow suggestion click to register
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const loadUserInfo = async () => {
    try {
      let user: any = null;
      if (Platform.OS === 'web') {
        const { UnifiedAuthService } = await import('../services/unifiedAuthService');
        user = await UnifiedAuthService.getCurrentUser();
      } else {
        user = await AuthManager.getSavedUser();
      }

      if (user) {
        let phone = '';
        if (user.phone) {
          phone = user.phone;
        } else if (typeof user.address === 'object' && user.address?.phone) {
          phone = user.address.phone;
        }

        setUserInfo({
          name: user.name || '',
          phone: phone,
          email: user.email || user.username || '',
          userId: user.id || user.uid || `user_${Date.now()}`,
        });
      }
    } catch (error) {
      console.error('Error loading user info:', error);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!service.trim()) {
      Alert.alert('Error', 'Please enter the service you need.');
      return;
    }

    if (!date.trim()) {
      Alert.alert('Error', 'Please select a date.');
      return;
    }

    if (!time.trim()) {
      Alert.alert('Error', 'Please select a time.');
      return;
    }

    if (!userInfo.name || !userInfo.phone) {
      Alert.alert('Error', 'Please complete your contact information.');
      return;
    }

    if (isAutomotiveRequest) {
      const automotiveError = validateAutomotiveIntake(automotiveIntake);
      if (automotiveError) {
        Alert.alert('Vehicle details required', automotiveError);
        return;
      }
    }

    setLoading(true);

    try {
      // Extract zipcode from business address or use default
      const zipcodeMatch = business.address?.match(/\b\d{5}\b/);
      const zipcode = zipcodeMatch ? zipcodeMatch[0] : '';

      if (requestType === 'estimate' && business.isOnBookAPT === true) {
        await customServiceRequestService.createRequest({
          userId: userInfo.userId,
          userName: userInfo.name,
          userPhone: userInfo.phone,
          userEmail: userInfo.email,
          businessId: business.id,
          businessName: business.name,
          serviceName: service.trim(),
          description: specialRequests.trim() || `Estimate requested for ${service.trim()} at ${business.name}`,
          preferredDate: date.trim(),
          preferredTime: time.trim(),
          additionalDetails: `Business: ${business.name} (${business.id})`,
          location: business.address,
          zipcode: zipcode,
          requestSubtype: 'standard',
          status: 'pending',
        });
        Alert.alert(
          '✅ Estimate Request Sent!',
          `Your estimate request was sent to ${business.name}. We'll notify you when they respond with a quote.`,
          [{ text: 'OK', onPress: () => onSubmit() }]
        );
        onSubmit();
        return;
      }

      const serviceLabel = requestType === 'estimate' ? `Estimate Request: ${service.trim()}` : service.trim();
      const baseSpecialRequests = requestType === 'estimate'
        ? `Please provide an estimate/quote before booking.${specialRequests.trim() ? ` Notes: ${specialRequests.trim()}` : ''}`
        : (specialRequests.trim() || '');
      const prefixedSpecialRequests = isAutomotiveRequest
        ? appendAutomotiveDetailsToInstructions(baseSpecialRequests, automotiveIntake)
        : (baseSpecialRequests || undefined);

      const result = await appointmentRequestService.createRequest({
        userId: userInfo.userId,
        userName: userInfo.name,
        userPhone: userInfo.phone,
        userEmail: userInfo.email,
        businessName: business.name,
        businessPhone: business.phone || '',
        businessEmail: business.email,
        service: serviceLabel,
        preferredDate: date.trim(),
        preferredTime: time.trim(),
        specialRequests: prefixedSpecialRequests,
        businessCategory: business.category,
        businessLocation: business.address,
        zipcode: zipcode,
      });

      if (result.success) {
        // Show success message with SMS confirmation
        Alert.alert(
          requestType === 'estimate' ? '✅ Estimate Request Sent!' : '✅ Request Sent!',
          `We've sent your ${requestType === 'estimate' ? 'estimate request' : 'appointment request'} via SMS to ${business.name}.\n\n` +
          `Status: Pending Business Confirmation\n\n` +
          `We'll notify you as soon as they respond. You can check your appointments to see the status.`,
          [
            {
              text: 'View My Appointments',
              onPress: () => {
                onSubmit();
                // The parent component will handle navigation
              }
            },
            {
              text: 'OK',
              style: 'cancel',
              onPress: () => onSubmit()
            }
          ]
        );
        onSubmit();
      } else {
        // Check if phone is not SMS-capable
        if (result.phoneNotSmsCapable) {
          const validationResult = result.validationResult;
          const carrierType = validationResult?.carrierType || 'unknown';
          
          let message = result.message || 'This phone number cannot receive SMS messages.';
          const buttons: any[] = [];
          
          // Add contact directly option
          if (business.phone) {
            buttons.push({
              text: 'Call Directly',
              onPress: async () => {
                try {
                  const phoneUrl = `tel:${business.phone}`;
                  const canOpen = await Linking.canOpenURL(phoneUrl);
                  if (canOpen) {
                    await Linking.openURL(phoneUrl);
                  } else {
                    Alert.alert('Cannot Make Call', `Please call ${business.name} at ${business.phone} manually.`);
                  }
                } catch (error) {
                  Alert.alert('Error', `Please call ${business.name} at ${business.phone} manually.`);
                }
              }
            });
          }
          
          // Add email option if available
          if (business.email) {
            buttons.push({
              text: 'Send Email Instead',
              onPress: () => {
                // TODO: Implement email invitation flow
                Alert.alert('Email Invitation', 'Email invitation feature coming soon!');
              }
            });
          }
          
          buttons.push({ text: 'OK', style: 'cancel' });
          
          Alert.alert(
            '⚠️ Cannot Send SMS',
            message,
            buttons
          );
        } 
        // Check if cap was reached
        else if (result.reachedCap) {
          Alert.alert(
            'Request Limit Reached',
            result.message || 'This business has reached the free request limit.',
            [
              { text: 'Cancel', style: 'cancel' },
              { 
                text: 'Invite to BookAPT', 
                onPress: () => {
                  // TODO: Navigate to invite flow or show invite option
                  Alert.alert('Invite Business', 'Invitation feature coming soon!');
                }
              }
            ]
          );
        } 
        // Other errors
        else {
          Alert.alert('Error', result.message || 'Failed to send appointment request.');
        }
        setLoading(false);
      }
    } catch (error) {
      console.error('Error submitting appointment request:', error);
      Alert.alert('Error', 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get minimum date (today)
  const minDate = getTodayDate();

  return (
    <>
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={onCancel} style={styles.cancelButton}>
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{requestType === 'estimate' ? 'Request Estimate' : 'Request Booking'}</Text>
        <View style={styles.placeholder} />
      </View>

      <View style={styles.content}>
        {/* Business Info */}
        <View style={styles.businessInfo}>
          {business.name && typeof business.name === 'string' && business.name.trim().length > 0 ? (
            <Text style={styles.businessName}>{business.name}</Text>
          ) : (
            <Text style={styles.businessName}>Unknown Business</Text>
          )}
          {business.address && typeof business.address === 'string' && business.address.trim().length > 0 && (
            <Text style={styles.businessAddress}>{business.address}</Text>
          )}
          {business.phone && typeof business.phone === 'string' && business.phone.trim().length > 0 && (
            <Text style={styles.businessPhone}>📞 {business.phone}</Text>
          )}
        </View>

        {/* Form Fields */}
        <View style={styles.formSection}>
          <Text style={styles.label}>{requestType === 'estimate' ? 'Service / Issue *' : 'Service Needed *'}</Text>
          <View style={styles.serviceInputContainer}>
            <AppTextInput
              style={styles.input}
              placeholder="e.g., Oil Change, Haircut, Consultation"
              value={service}
              onChangeText={handleServiceChange}
              onFocus={() => {
                if (serviceSuggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              onBlur={handleBlur}
              autoCapitalize="words"
            />
            {showSuggestions && filteredSuggestions.length > 0 && (
              <View style={styles.suggestionsContainer}>
                <FlatList
                  data={filteredSuggestions.slice(0, 5)} // Show max 5 suggestions
                  keyExtractor={(item, index) => `${item}-${index}`}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      style={styles.suggestionItem}
                      onPress={() => handleSuggestionSelect(item)}
                    >
                      <Text style={styles.suggestionText}>{item}</Text>
                    </TouchableOpacity>
                  )}
                  style={styles.suggestionsList}
                />
              </View>
            )}
          </View>
          {serviceSuggestions.length > 0 && !showSuggestions && (
            <Text style={styles.hint}>
              💡 Suggestions: {serviceSuggestions.slice(0, 3).join(', ')}
            </Text>
          )}

          <Text style={styles.label}>Preferred Date *</Text>
          <DatePicker
            value={date}
            onChange={setDate}
            minDate={minDate}
            placeholder="Select date"
          />

          <Text style={styles.label}>Preferred Time *</Text>
          <TimePicker
            value={time}
            onChange={setTime}
            placeholder="Select time"
          />

          {isAutomotiveRequest ? (
            <AutomotiveServiceIntakeForm
              value={automotiveIntake}
              onChange={handleAutomotiveIntakeChange}
              savedVehicles={userInfo.userId ? savedVehicles : []}
              selectedSavedVehicleId={userInfo.userId ? selectedAutomotiveVehicleId : null}
              onSelectSavedVehicle={
                userInfo.userId ? handleSelectSavedVehicleForIntake : undefined
              }
              onChooseManualEntry={
                userInfo.userId ? handleChooseManualAutomotiveEntry : undefined
              }
              onManageVehicles={userInfo.userId ? () => setShowVehiclesModal(true) : undefined}
              compact
            />
          ) : null}

          {!isAutomotiveRequest ? (
          <>
          <Text style={styles.label}>{requestType === 'estimate' ? 'Problem Details (Optional)' : 'Special Requests (Optional)'}</Text>
          <AppTextInput
            style={[styles.input, styles.textArea]}
            placeholder={requestType === 'estimate' ? 'Describe the issue, symptoms, and any details that help quote accurately...' : 'Any special instructions or requests...'}
            value={specialRequests}
            onChangeText={setSpecialRequests}
            multiline
            numberOfLines={4}
            textAlignVertical="top"
          />
          </>
          ) : null}
        </View>

        {/* Your Contact Info */}
        <View style={styles.contactSection}>
          <Text style={styles.sectionTitle}>Your Contact Information</Text>
          
          <Text style={styles.label}>Name *</Text>
          <AppTextInput
            style={styles.input}
            placeholder="Your name"
            value={userInfo.name}
            onChangeText={(text) => setUserInfo({ ...userInfo, name: text })}
          />

          <Text style={styles.label}>Phone *</Text>
          <AppTextInput
            style={styles.input}
            placeholder="(555) 123-4567"
            value={userInfo.phone}
            onChangeText={(text) => setUserInfo({ ...userInfo, phone: text })}
            keyboardType="phone-pad"
          />

          <Text style={styles.label}>Email</Text>
          <AppTextInput
            style={styles.input}
            placeholder="your@email.com"
            value={userInfo.email}
            onChangeText={(text) => setUserInfo({ ...userInfo, email: text })}
            keyboardType="email-address"
            autoCapitalize="none"
          />
        </View>

        {/* Info Box */}
        <View style={styles.infoBox}>
          <Text style={styles.infoBoxText}>
            📞 We'll send your {requestType === 'estimate' ? 'estimate request' : 'booking request'} to {business.name} via SMS.
            They'll be able to {requestType === 'estimate' ? 'reply with an estimate or ask follow-up questions' : 'confirm, decline, or suggest a different time'}.
            They'll also receive an invitation to join BookAPT to get more bookings automatically!
          </Text>
        </View>

        {/* Submit Button */}
        <TouchableOpacity
          style={[styles.submitButton, loading && styles.submitButtonDisabled]}
          onPress={handleSubmit}
          disabled={loading}
        >
          <Text style={styles.submitButtonText}>
            {loading ? 'Sending...' : (requestType === 'estimate' ? 'Send Estimate Request' : 'Send Booking Request')}
          </Text>
        </TouchableOpacity>
      </View>
    </ScrollView>

    {userInfo.userId ? (
      <MyVehiclesModal
        visible={showVehiclesModal}
        userId={userInfo.userId}
        selectedVehicleId={selectedAutomotiveVehicleId}
        onSelectVehicleId={async (id) => {
          setShowVehiclesModal(false);
          try {
            const list = await vehicleGarageService.listVehicles(userInfo.userId);
            setSavedVehicles(list);
            const vehicle = list.find((v) => v.id === id);
            if (vehicle) {
              handleSelectSavedVehicleForIntake(vehicle);
            }
          } catch {
            /* ignore */
          }
        }}
        onClose={async () => {
          setShowVehiclesModal(false);
          try {
            const list = await vehicleGarageService.listVehicles(userInfo.userId);
            setSavedVehicles(list);
          } catch {
            /* ignore */
          }
        }}
      />
    ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  cancelButton: {
    padding: 8,
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#007AFF',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  placeholder: {
    width: 60,
  },
  content: {
    padding: 16,
  },
  businessInfo: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  businessName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  businessAddress: {
    fontSize: 14,
    color: '#666',
    marginBottom: 4,
  },
  businessPhone: {
    fontSize: 14,
    color: '#666',
  },
  formSection: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginTop: 12,
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fff',
  },
  textArea: {
    height: 100,
    paddingTop: 12,
  },
  hint: {
    fontSize: 12,
    color: '#999',
    marginTop: 4,
    marginBottom: 8,
  },
  contactSection: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  infoBox: {
    backgroundColor: '#e7f3ff',
    padding: 12,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#007AFF',
    marginBottom: 16,
  },
  infoBoxText: {
    fontSize: 14,
    color: '#333',
    lineHeight: 20,
  },
  submitButton: {
    backgroundColor: '#28a745',
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  submitButtonDisabled: {
    backgroundColor: '#ccc',
  },
  submitButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  serviceInputContainer: {
    position: 'relative',
    zIndex: 1,
  },
  suggestionsContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    marginTop: 4,
    maxHeight: 200,
    zIndex: 1000,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  suggestionsList: {
    maxHeight: 200,
  },
  suggestionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  suggestionText: {
    fontSize: 14,
    color: '#333',
  },
});

export default AppointmentRequestForm;

